import { lazy, Suspense } from 'react';
import type { MapViewProps } from './MapViewImpl';

/**
 * Sprint BD (2026-05-20, R-5.1.6 バンドル削減 / Phase 5 carry-over): MapView を
 * **React.lazy 化された wrapper** に置換。実装本体 (`MapViewImpl.tsx`) は dynamic
 * import 経由でのみロードされ、maplibre-gl + pmtiles + maplibre-gl CSS / PMTiles
 * protocol module (合計 ~700-900KB) は MapPage / PostForm 到達時の初回 render まで
 * main chunk から切り離される。
 *
 * 互換:
 *   - public API (`MapViewProps`) は完全維持 — caller 側 (MapPage 等) 変更不要
 *   - Suspense fallback は spinner のみ表示 (旧 UX 踏襲、tile fetch 待ちの間も
 *     MapView 内部で同じ spinner を見せていた)
 *
 * 注意:
 *   - 本ファイルから maplibre-gl への直接参照は禁止。すべて MapViewImpl 経由
 *   - vite.config.ts の manualChunks で 'maplibre-vendor' chunk に強制 (本 sprint 対応済)
 */

// re-export type for external callers (`import { MapView, type MapViewProps } from '../map'`).
export type { MapViewProps, GisOverlayLayer, GisFeatureClickInfo } from './MapViewImpl';

const LazyMapView = lazy(() => import('./MapViewImpl'));

function MapViewFallback() {
  return (
    <div className="relative h-full w-full flex items-center justify-center bg-slate-100">
      <div
        className="w-8 h-8 border-2 border-slate-300 border-t-primary-600 rounded-full animate-spin"
        role="status"
        aria-label="地図を読み込み中"
      />
    </div>
  );
}

export function MapView(props: MapViewProps) {
  return (
    <Suspense fallback={<MapViewFallback />}>
      <LazyMapView {...props} />
    </Suspense>
  );
}
